import { useRef } from 'react';
import { Alert, CircularProgress, Stack } from '@mui/material';
import { AvlTable } from '../../Shared/Table/Table';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { green } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DownloadIcon from '@mui/icons-material/Download';

export const Columns = {
    name: 'Name',
    action: 'Action',
};

function ExperimentEntitiesTable(
    {
        onDocumentDelete,
        onDownload,
        onPreview,
        documents,
    },
) {
    const deleteButtonRefs = useRef([]);
    const downloadButtonRefs = useRef([]);
    const deleteSpinnerRefs = useRef([]);
    const downloadSpinnerRefs = useRef([]);

    const handleDelete = async (documentId, index) => {
        deleteButtonRefs.current[index].style.disabled = true;
        deleteSpinnerRefs.current[index].style.display = 'block';
        deleteButtonRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
        deleteButtonRefs.current[index].style.color = 'rgba(0, 0, 0, 0.26)';
        deleteButtonRefs.current[index].style.borderColor = 'rgba(0, 0, 0, 0.26)';

        onDocumentDelete?.(documentId);
    };

    const handleDownload = async (row, index) => {
        downloadButtonRefs.current[index].style.disabled = true;
        downloadSpinnerRefs.current[index].style.display = 'block';
        downloadButtonRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
        downloadButtonRefs.current[index].style.color = 'rgba(0, 0, 0, 0.26)';
        downloadButtonRefs.current[index].style.borderColor = 'rgba(0, 0, 0, 0.26)';

        await onDownload?.(row);

        setTimeout(() => {
            downloadButtonRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
            downloadButtonRefs.current[index].style.color = 'rgb(25, 118, 210)';
            downloadButtonRefs.current[index].style.borderColor = 'rgb(25, 118, 210)';
            downloadButtonRefs.current[index].style.disabled = false;
            downloadSpinnerRefs.current[index].style.display = 'none';
        }, 2000);
    };

    const tableHeaders = [
        {
            label: Columns.name,
            align: 'left',
        },
        {
            label: Columns.action,
            align: 'center',
        },
    ];

    const convertToCells = (row, rowIndex) => [
        {
            style: { width: '100%', padding: '0 0 0 16px' },
            data: row.file_name || 'Unknown document',
        },
        {
            style: { padding: 0 },
            align: 'center',
            data: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {onPreview && (
                        <Box key={`id${row.id}-preview`} sx={{ position: 'relative' }}>
                            <Tooltip title={row.has_preview ? 'Preview' : 'There is no preview'}>
                                <span>
                                    <IconButton
                                        disabled={!row.has_preview}
                                        onClick={() => onPreview?.(row.id)}
                                    >
                                        <VisibilityRoundedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    {onDownload && (
                        <Box key={`id${row.id}-download`} sx={{ position: 'relative' }}>
                            <Tooltip title="Download">
                                <span>
                                    <IconButton
                                        onClick={() => handleDownload(row, rowIndex)}
                                        ref={(el) => (downloadButtonRefs.current[rowIndex] = el)}
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <CircularProgress
                                size={24}
                                ref={(el) => (downloadSpinnerRefs.current[rowIndex] = el)}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                    display: 'none',
                                }}
                            />
                        </Box>
                    )}
                    <Box key={`id${row.id}`} sx={{ position: 'relative' }}>
                        <Tooltip title="Delete">
                            <span>
                                <IconButton
                                    id={'delete' + `id${row.id}`}
                                    ref={(el) => (deleteButtonRefs.current[`id${row.id}`] = el)}
                                    onClick={() => handleDelete(row.id, `id${row.id}`)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <CircularProgress
                            size={24}
                            ref={(el) => (deleteSpinnerRefs.current[`id${row.id}`] = el)}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                                display: 'none',
                            }}
                        />
                    </Box>
                </div>
            ),
        },
    ];

    const tableRows = documents.map((row, rowIndex) => ({
        cells: convertToCells(row, rowIndex),
    }));

    return documents.length > 0
        ? (
            <AvlTable
                isPaginationDisabled
                headers={tableHeaders}
                rows={tableRows}
            />
        )
        : (
            <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="warning">Records not found!</Alert>
            </Stack>
        );
}

export default ExperimentEntitiesTable;
